import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface SwipeHintProps {
  className?: string;
  showText?: boolean;
}

/** Static swipe hint: bare chevrons + muted text. Hides after 4s or on first touch. */
const SwipeHint = ({ className, showText = true }: SwipeHintProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    // Check if user has already seen the hint
    const hasSeenHint = sessionStorage.getItem("swipe-hint-seen");
    if (hasSeenHint) {
      setIsVisible(false);
      setHasInteracted(true);
      return;
    }

    // Hide hint after 4 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      sessionStorage.setItem("swipe-hint-seen", "true");
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const handleInteraction = () => {
    if (!hasInteracted) {
      setIsVisible(false);
      setHasInteracted(true);
      sessionStorage.setItem("swipe-hint-seen", "true");
    }
  };

  if (!isVisible || hasInteracted) return null;

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center py-3 md:hidden",
        className
      )}
      onTouchStart={handleInteraction}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <ChevronLeft className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        {showText && <span className="text-xs font-medium">Swipe to see more</span>}
        <ChevronRight className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </div>
    </div>
  );
};

export default SwipeHint;
